/**
 * DevTools guard — production only (patientapp.shalamarhospital.org.pk + apis.sihs.org.pk).
 *
 * Whole-tab lock, similar to how sites like FBR Pakistan handle this: when
 * DevTools are detected (including already open before navigation, i.e.
 * opened before the user ever visits the site), the tab is blocked with a
 * native browser `prompt()` — the slim bar Chrome pins under the address bar
 * — instead of an in-page form.
 *
 * The lock tracks LIVE DevTools state, in both directions, for as long as
 * the tab stays open:
 *   - DevTools confirmed open  → the tab locks (cover + password prompt).
 *   - DevTools confirmed closed afterwards → the tab unlocks automatically,
 *     no password required. Detection keeps running in the background even
 *     while the cover is showing, specifically to catch this.
 *   - DevTools opened again later → the tab locks again, fresh, exactly
 *     like the first time.
 * The correct admin password grants a standing bypass for the rest of the
 * tab session (debugging allowed without being re-prompted), but simply
 * closing DevTools — without ever giving a password — is enough on its own
 * to regain normal access. Nothing is ever persisted across page loads to
 * force a password: closing the tab, or DevTools, always returns things to
 * a clean slate.
 *
 * Wrong password, an empty/blank "OK", and Cancel are all treated
 * identically (best-effort navigate the tab to about:blank, exactly like
 * closing out of the page) — none of them unlock anything. Only an exact
 * password match does. Nothing in localStorage/sessionStorage or the Redux
 * store is touched by any of this, so the user's login session is always
 * left intact — reopening the app (in this tab or a new one) once DevTools
 * is closed drops them right back in signed in, no re-login needed.
 *
 * Native dialogs are not the sole enforcement, for a specific, well-
 * documented reason: Chrome silently disables ALL future alert/confirm/
 * prompt dialogs for a tab's remaining lifetime once it decides a page is
 * spamming them — multiple dialogs back-to-back with no fresh user click in
 * between trips this. After that, `prompt()` returns `null` instantly with
 * no UI at all, indistinguishable from Cancel. To avoid ever triggering
 * that: exactly one dialog is shown automatically the instant a lock first
 * engages; every attempt after that (from a wrong password, Cancel, or a
 * suppressed dialog) requires a fresh click on the cover's own button
 * before another `prompt()` is shown — dialogs are never chained
 * automatically. If one does come back suspiciously fast (< 50ms, timed
 * around the call), that's recognized as "the browser blocked this dialog"
 * rather than misread as a real Cancel, and surfaced as an explicit
 * message. Either way the full-screen, undismissable cover — not the
 * dialog — is what actually blocks the app, so a suppressed dialog never
 * translates into free access while DevTools stays open; it only ever
 * clears once DevTools is confirmed closed by the ongoing background checks
 * or the correct password is entered.
 * A lightweight MutationObserver re-attaches the cover if it is ever removed
 * from the DOM while a lock is in effect.
 *
 * Detection does NOT use a `debugger` statement / timing trick (no "pause on
 * debugger" probe) — that pauses JS execution whenever DevTools has
 * breakpoints/an open panel, which is an inconsistent, jumpy way to detect
 * things. Instead detection relies purely on passive, non-blocking signals:
 *   - Docked-window size heuristic (outer vs inner viewport delta) — skipped
 *     on a genuine Android/iOS browser, where a virtual keyboard or rotation
 *     produces the same size delta as a docked panel would (see "Platform
 *     detection" below)
 *   - Device-toolbar heuristic — Chrome's DevTools mobile/responsive preview
 *     forces `outerWidth === innerWidth` and pins `maxTouchPoints` to 1,
 *     which is otherwise invisible to the size heuristic above; that exact
 *     combination flags it (catches DevTools' "mobile view" on desktop)
 *   - Console inspection probe (getter fires only while the inspector is
 *     attached and formatting logged values — true whether DevTools is
 *     docked, undocked in its own window, or was opened before this script
 *     ever ran, and regardless of which panel — Elements/Console/etc — is
 *     active)
 * Real mobile OS browsers (Android/iOS) have no built-in DevTools UI, so
 * genuine phones/tablets are recognized (by UA + real multi-touch — see
 * "Platform detection") and only get the console probe, never the two
 * window-size heuristics above.
 * All checks run on an interval starting immediately at boot (before React
 * even mounts), so DevTools opened prior to the visit is caught on the very
 * first checks, not just after the user interacts with the page.
 *
 * Does not block right-click or keyboard shortcuts — detection handles access.
 *
 * Env:
 *   REACT_APP_DEVTOOLS_MASTER_PASSWORD — admin password (quote if it has spaces/#)
 *   REACT_APP_DEVTOOLS_GUARD_TEST=true  — ONLY enables localhost testing
 *     (false does not disable production; production uses hostname / API URL)
 */

import config from '../config';

const PROD_APP_HOST = 'patientapp.shalamarhospital.org.pk';
const PROD_API_HOST = 'apis.sihs.org.pk';
const UNLOCK_SESSION_KEY = '__pp_dt_unlocked';

/** Consecutive matching poll ticks required before flipping lock state, in either direction. */
const CONFIRM_HITS = 2;
const POLL_MS = 350;
/** A prompt() returning this fast means the browser suppressed it rather than the user answering it. */
const SUPPRESSED_DIALOG_MS = 50;

const LOCK_MESSAGE = 'Developer Tools detected.\n\nEnter the admin password to continue working.';

/**
 * Password is inlined at `npm run build` time from .env (local builds include it).
 * Fallback is only a safety net if the var is ever missing.
 */
const MASTER_PASSWORD = (
  process.env.REACT_APP_DEVTOOLS_MASTER_PASSWORD ||
  'production@#i2#**web** '
);

/** ONLY affects localhost / 127.0.0.1 — not production. */
const LOCAL_TEST_ENABLED =
  String(process.env.REACT_APP_DEVTOOLS_GUARD_TEST || '').toLowerCase() === 'true';

function isLocalHost(host) {
  return host === 'localhost' || host === '127.0.0.1';
}

function isProdAppHost(host) {
  return (
    host === PROD_APP_HOST ||
    host === `www.${PROD_APP_HOST}` ||
    host.endsWith(`.${PROD_APP_HOST}`)
  );
}

function isProdApiConfigured() {
  try {
    return Object.values(config).some(
      (value) => typeof value === 'string' && value.toLowerCase().includes(PROD_API_HOST)
    );
  } catch {
    return false;
  }
}

function shouldEnableGuard() {
  const host = (window.location.hostname || '').toLowerCase();

  // Explicit local test switch (REACT_APP_DEVTOOLS_GUARD_TEST=true)
  if (LOCAL_TEST_ENABLED && isLocalHost(host)) {
    return true;
  }

  // Never auto-enable on local / preview / test-api hosts
  if (isLocalHost(host) || host.includes('netlify') || host.includes('testapi')) {
    return false;
  }

  // Production app URL: https://patientapp.shalamarhospital.org.pk/
  if (isProdAppHost(host)) {
    return true;
  }

  // Production API build on any other hospital / sihs host
  if (
    isProdApiConfigured() &&
    (host.endsWith('shalamarhospital.org.pk') || host.endsWith('sihs.org.pk'))
  ) {
    return true;
  }

  return false;
}

/** Phone number is the patient-portal user identity (no auth.userId in this app). */
function getCurrentUserId(store) {
  try {
    const fromStorage = localStorage.getItem('phoneNumber');
    if (fromStorage) return fromStorage;
  } catch {
    /* ignore */
  }
  return store?.getState()?.user?.selectedUser?.Phone || null;
}

function isUnlocked() {
  try {
    const raw = sessionStorage.getItem(UNLOCK_SESSION_KEY);
    if (!raw) return false;
    // Value is "1" (legacy) or a userId — legacy "1" still counts as unlocked
    // until logout clears it; new unlocks are bound to userId below.
    return raw.length > 0;
  } catch {
    return false;
  }
}

function setUnlocked(userId) {
  try {
    // Bind unlock to the current user so a later login on the same tab is not trusted
    sessionStorage.setItem(UNLOCK_SESSION_KEY, String(userId || '1'));
  } catch {
    /* ignore */
  }
}

/** Clear admin unlock — also invoked from the Redux logout path. */
export function clearDevToolsUnlock() {
  try {
    sessionStorage.removeItem(UNLOCK_SESSION_KEY);
  } catch {
    /* ignore */
  }
}

function unlockMatchesCurrentUser(userId) {
  try {
    const raw = sessionStorage.getItem(UNLOCK_SESSION_KEY);
    if (!raw) return false;
    // Legacy unlock before user-binding
    if (raw === '1') return true;
    if (!userId) return false;
    return raw === String(userId);
  } catch {
    return false;
  }
}

// ── State ──────────────────────────────────────────────────────────────────────

let overlayEl = null;
let lockEngaged = false;
let attemptInFlight = false;
let mutationObserver = null;
let consecutiveOpenHits = 0;
let consecutiveClosedHits = 0;
let consoleDetector = null;
let storeRef = null;

function getStore() {
  if (storeRef) return Promise.resolve(storeRef);
  return import('../store/store').then(({ default: store }) => {
    storeRef = store;
    return store;
  });
}

// ── Platform detection ───────────────────────────────────────────────────────────
//
// Real Android/iOS Chrome/Safari has no built-in DevTools UI at all, so the
// guard's window-size heuristics are both unnecessary AND actively harmful
// there — a phone's on-screen keyboard or orientation change resizes the
// viewport the same way a docked DevTools panel would, which would otherwise
// lock the tab on an ordinary mobile user typing into a form. Desktop Chrome,
// however, must stay covered even when its DevTools "device toolbar" is used
// to preview a mobile layout — Chrome makes that mode intentionally hard to
// distinguish from a real phone (it overrides the user agent, touch support,
// and viewport size), but it always forces `navigator.maxTouchPoints` to
// exactly 1 while emulating, whereas genuine modern touchscreens (phones,
// tablets, and touch-laptops) report real multi-touch (commonly 5 or 10).
// That one detail is not spoofed by default device-toolbar emulation and is
// what lets us tell the two apart reliably.

function getMaxTouchPoints() {
  const n = typeof navigator !== 'undefined' ? navigator.maxTouchPoints : 0;
  return typeof n === 'number' && n >= 0 ? n : 0;
}

/** iPadOS 13+ Safari reports a desktop "Macintosh" UA — real Macs have no touchscreen at all. */
function isIPadMasqueradingAsMac() {
  const ua = (navigator.userAgent || '').toLowerCase();
  return ua.includes('macintosh') && getMaxTouchPoints() > 1;
}

function hasMobileLikeUA() {
  const ua = navigator.userAgent || '';
  return /android|iphone|ipod|ipad|windows phone|blackberry|iemobile|mobile/i.test(ua);
}

/**
 * True only for a genuine Android/iOS browser — never for desktop Chrome's
 * DevTools device-toolbar emulation of one. See rationale above: emulation
 * always pins `maxTouchPoints` to 1, while real touch hardware reports > 1.
 */
function isRealMobileDevice() {
  if (typeof navigator === 'undefined') return false;
  if (!hasMobileLikeUA() && !isIPadMasqueradingAsMac()) return false;
  return getMaxTouchPoints() > 1;
}

/**
 * True while Chrome's DevTools "device toolbar" (responsive/mobile preview)
 * is open. In that mode Chrome always makes `outerWidth === innerWidth` and
 * `outerHeight === innerHeight` (it simulates a device with no browser
 * chrome at all — see isDevToolsOpenByDock below), which is exactly why the
 * size-diff heuristic alone goes blind to it. Pairing that with
 * `maxTouchPoints === 1` (always forced by the emulator, see above) turns
 * that blind spot into a positive, independent signal instead: a real
 * desktop window essentially never reports zero chrome AND single-touch at
 * the same time, so this fires reliably for the exact scenario that would
 * otherwise slip past the guard, without depending on the UA string at all.
 */
function isDeviceToolbarActive() {
  const outerW = window.outerWidth;
  const outerH = window.outerHeight;
  if (!outerW || !outerH) return false;
  const noChromeOffset = outerW === window.innerWidth && outerH === window.innerHeight;
  return noChromeOffset && getMaxTouchPoints() === 1;
}

// ── Detection ──────────────────────────────────────────────────────────────────

/**
 * Docked DevTools: one side grows a lot; zoom grows both — require asymmetry.
 * Skipped on real mobile devices (see platform detection above) since a
 * virtual keyboard or rotation there triggers the exact same size pattern.
 */
function isDevToolsOpenByDock() {
  if (isRealMobileDevice()) return false;

  const outerW = window.outerWidth;
  const outerH = window.outerHeight;
  if (!outerW || !outerH) return false;

  const widthDiff = outerW - window.innerWidth;
  const heightDiff = outerH - window.innerHeight;
  if (widthDiff < 0 || heightDiff < 0) return false;

  const sideDocked = widthDiff > 200 && widthDiff > heightDiff * 1.5;
  const bottomDocked = heightDiff > 200 && heightDiff > widthDiff * 1.5;
  return sideDocked || bottomDocked;
}

function createConsoleDetector() {
  let triggered = false;

  const img = new Image();
  Object.defineProperty(img, 'id', {
    configurable: true,
    get() {
      triggered = true;
      return 'dt';
    },
  });

  const obj = {};
  Object.defineProperty(obj, 'dtProbe', {
    configurable: true,
    enumerable: true,
    get() {
      triggered = true;
      return true;
    },
  });

  return {
    check() {
      triggered = false;
      try {
        // eslint-disable-next-line no-console
        console.log(img);
        // eslint-disable-next-line no-console
        console.dir(obj);
      } catch {
        return false;
      }
      return triggered;
    },
  };
}

/**
 * Combines the passive, non-blocking detection signals. Deliberately does
 * NOT use a `debugger` statement / timing probe — that pauses real script
 * execution whenever DevTools is open (freezing the tab), which is the
 * behavior this guard must avoid while still reliably detecting DevTools.
 */
function isDevToolsOpen() {
  if (isDeviceToolbarActive()) return true;
  if (isDevToolsOpenByDock()) return true;
  if (consoleDetector && consoleDetector.check()) return true;
  return false;
}

// ── Whole-tab lock ─────────────────────────────────────────────────────────────
//
// An undismissable full-screen cover with a single "Enter Password" button
// as its only interactive element. Its own text updates in place for
// feedback (wrong password, suppressed dialogs); it never triggers a dialog
// on its own except in direct response to that button being clicked, so
// every `prompt()` call (after the first automatic one) is tied to a fresh
// user gesture — see header comment for why that matters. The cover has no
// "close"/"dismiss" control of its own — it only ever comes down for the
// correct password or a confirmed DevTools-closed state (see `evaluate` in
// `initDevToolsGuard` below).

function ensureOverlay() {
  if (overlayEl) return overlayEl;

  overlayEl = document.createElement('div');
  overlayEl.setAttribute('data-devtools-guard', '1');
  Object.assign(overlayEl.style, {
    position: 'fixed',
    inset: '0',
    zIndex: '2147483647',
    background: '#0f172a',
    display: 'none',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
    padding: '24px',
    boxSizing: 'border-box',
    userSelect: 'none',
  });

  overlayEl.innerHTML = `
    <svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
      <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
    </svg>
    <p style="color:#f1f5f9;font-size:20px;font-weight:700;margin:0;text-align:center">
      Tab Locked — Developer Tools Detected
    </p>
    <p id="dt-guard-msg" style="color:#94a3b8;font-size:14px;margin:0;text-align:center;max-width:380px;line-height:1.45;min-height:40px">
      Click below to enter the admin password and continue.
    </p>
    <button type="button" id="dt-guard-btn" style="padding:11px 20px;border:none;border-radius:8px;background:#2563eb;color:#fff;font-weight:600;cursor:pointer;font-size:14px">
      Enter Password
    </button>
  `;

  document.documentElement.appendChild(overlayEl);
  overlayEl.querySelector('#dt-guard-btn').addEventListener('click', (e) => {
    e.preventDefault();
    attemptUnlock();
  });

  return overlayEl;
}

function setOverlayMessage(text) {
  const el = overlayEl?.querySelector('#dt-guard-msg');
  if (el) el.textContent = text;
}

/** Self-healing: re-attach the cover if it's ever removed from the DOM while locked. */
function watchOverlay() {
  if (mutationObserver) return;
  mutationObserver = new MutationObserver(() => {
    if (lockEngaged && overlayEl && !document.documentElement.contains(overlayEl)) {
      document.documentElement.appendChild(overlayEl);
    }
  });
  mutationObserver.observe(document.documentElement, { childList: true });
}

/**
 * Best-effort only — not the enforcement mechanism (see header comment).
 * Leaves the tab without touching any stored session/auth data; reopening
 * the app (this tab once DevTools closes, or a new tab any time) leaves the
 * user signed in.
 */
function lockTabToBlank() {
  try {
    window.location.replace('about:blank');
  } catch {
    try {
      window.open('about:blank', '_self');
    } catch {
      /* ignore — the full-screen cover still blocks the app either way */
    }
  }
}

/**
 * One password attempt, always tied to a real user click (or the single
 * automatic call made the instant a lock first engages). Never calls a
 * second dialog on its own — every retry after this one requires another
 * click, which is what keeps Chrome from ever deciding this tab is spamming
 * dialogs and silently disabling them (see header comment).
 *
 * Only an exact password match unlocks anything. Cancel, a blank "OK", a
 * wrong password, and a browser-suppressed dialog are all treated
 * identically: none of them unlock the tab, and all of them attempt the
 * same best-effort "leave the tab" action. The cover stays up regardless —
 * it only comes down for the correct password, or once the background
 * checks confirm DevTools has actually been closed.
 */
function attemptUnlock() {
  if (attemptInFlight || !lockEngaged) return;
  attemptInFlight = true;

  const startedAt = performance.now();
  let answer = null;
  try {
    answer = window.prompt(LOCK_MESSAGE);
  } catch {
    answer = null;
  }
  const elapsedMs = performance.now() - startedAt;
  attemptInFlight = false;

  if (MASTER_PASSWORD && answer !== null && answer !== '' && answer === MASTER_PASSWORD) {
    getStore()
      .then((store) => setUnlocked(getCurrentUserId(store)))
      .catch(() => setUnlocked(null));
    disengageLock();
    return;
  }

  const suppressed = answer === null && elapsedMs < SUPPRESSED_DIALOG_MS;
  setOverlayMessage(
    suppressed
      ? 'Your browser has blocked dialogs on this tab. Close DevTools, or close this tab, to continue.'
      : 'Click below to try again, or close DevTools to regain access automatically.'
  );
  // Cancel, a blank "OK", and a wrong password all land here and are handled
  // the same way as Cancel: best-effort leave the tab. If DevTools is still
  // open when/if the user comes back, the checks below re-lock it fresh.
  lockTabToBlank();
}

/**
 * Engages the whole-tab lock: shows the undismissable cover and makes one
 * automatic password attempt so the native prompt still appears
 * immediately, exactly like before. Every attempt after that requires a
 * fresh click on the cover's button. Nothing is persisted — this is purely
 * in-memory for the current DevTools-open episode, so it naturally clears
 * on the next full page load if DevTools happens to be closed by then.
 */
function engageLock() {
  if (lockEngaged) return;
  lockEngaged = true;
  consecutiveClosedHits = 0;
  ensureOverlay();
  setOverlayMessage('Click below to enter the admin password and continue.');
  overlayEl.style.display = 'flex';
  watchOverlay();
  attemptUnlock();
}

/**
 * Disengages the whole-tab lock — either because the correct password was
 * given, or because the background checks confirmed DevTools is now
 * closed. Either way access resumes immediately, no further action needed.
 */
function disengageLock() {
  lockEngaged = false;
  consecutiveOpenHits = 0;
  consecutiveClosedHits = 0;
  if (overlayEl) overlayEl.style.display = 'none';
}

// ── Boot ───────────────────────────────────────────────────────────────────────

export function initDevToolsGuard() {
  if (typeof window === 'undefined') return;
  if (!shouldEnableGuard()) return;
  if (!MASTER_PASSWORD) {
    // eslint-disable-next-line no-console
    console.warn('[DevToolsGuard] No master password configured; guard disabled.');
    return;
  }

  consoleDetector = createConsoleDetector();

  // Keep unlock in sync with auth: logout or user switch clears trust
  getStore()
    .then((store) => {
      let prevUserId = getCurrentUserId(store);
      let prevToken = store.getState()?.auth?.token || null;

      store.subscribe(() => {
        const auth = store.getState()?.auth || {};
        const userId = getCurrentUserId(store);
        const token = auth.token || null;

        if (prevToken && !token) {
          clearDevToolsUnlock();
        }
        if (prevUserId && userId && prevUserId !== userId) {
          clearDevToolsUnlock();
        }
        if (token && userId && !unlockMatchesCurrentUser(userId)) {
          const raw = sessionStorage.getItem(UNLOCK_SESSION_KEY);
          if (raw && raw !== '1') {
            clearDevToolsUnlock();
          }
        }

        prevUserId = userId;
        prevToken = token;
      });
    })
    .catch(() => {
      /* store not ready */
    });

  const evaluate = () => {
    if (isUnlocked()) {
      getStore()
        .then((store) => {
          const userId = getCurrentUserId(store);
          const token = store.getState()?.auth?.token || null;
          if (!token) {
            clearDevToolsUnlock();
            return;
          }
          if (userId && !unlockMatchesCurrentUser(userId)) {
            clearDevToolsUnlock();
          }
        })
        .catch(() => {});
      return;
    }

    // Detection keeps running even while the cover is showing — this is
    // what lets DevTools closing be noticed and auto-clear the lock below,
    // and what lets it re-lock, fresh, if DevTools is opened again later.
    const open = isDevToolsOpen();

    if (open) {
      consecutiveOpenHits += 1;
      consecutiveClosedHits = 0;
      if (consecutiveOpenHits >= CONFIRM_HITS) {
        engageLock();
      }
      return;
    }

    consecutiveClosedHits += 1;
    consecutiveOpenHits = 0;
    if (lockEngaged && consecutiveClosedHits >= CONFIRM_HITS) {
      // DevTools has genuinely closed — regain access automatically, no
      // password needed. Opening DevTools again will re-lock the tab fresh.
      disengageLock();
    }
  };

  evaluate();
  setTimeout(evaluate, 300);
  setInterval(evaluate, POLL_MS);
}
